"use client";
import FilterTag from "@components/atoms/FilterTag";
import React, { useState, useEffect } from "react";
import styles from "./FilterToggle.module.scss";
import Button from "@components/atoms/Button";
import SortModal from "@components/molecules/SortModal";
import ModalManager from "../ModalManager";
import { cn } from "@utils/utils";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";

type FilterConfig = {
  key: string;
  label: string;
  prefix?: string;
  modalTitle: string;
  options?: string[];
  multiSelect?: boolean;
  withPriceRange?: boolean;
  withClear?: boolean;
  maxPrice?: number; // Optional, used only if withPriceRange is true
};

interface FilterToggleProps {
  options?: string[];
  filterConfigs?: FilterConfig[];
  onApply?: (key: string, selectedValues: string[]) => void;
  onClear?: () => void;
  initialSelectedFilters?: Record<string, string[]>;
  wrapperClassName?: string;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  carAvailability?: string;
}

export default function FilterToggle({
  options,
  filterConfigs,
  onApply,
  onClear,
  initialSelectedFilters = {},
  wrapperClassName,
  pageType,
  brandName,
  modelName,
  carAvailability,
}: FilterToggleProps) {
  const { name: cityName } = getSelectedCityClient();
  const [selected, setSelected] = useState(options?.[0]);
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const [selectedMap, setSelectedMap] = useState<Record<string, string[]>>({});

  // Sync selectedMap with initialSelectedFilters from parent
  useEffect(() => {
    setSelectedMap(initialSelectedFilters);
  }, [initialSelectedFilters]);

  const handleApply = async (key: string, values: string[]) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selected_value: values.join(", "),
        filter_type: key,
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        city_name: cityName || "",
      },
    });

    setSelectedMap((prev) => ({ ...prev, [key]: values }));
    onApply?.(key, values);
    setActiveFilterKey(null);
  };

  const handleClear = () => {
    setSelected(options?.[0]);
    setSelectedMap({});
    onClear?.();
    setActiveFilterKey(null);
  };

  const activeFilter = filterConfigs?.find((f) => f.key === activeFilterKey);

  return (
    <div
      className={cn(
        styles.wrapper,
        wrapperClassName ? ` ${wrapperClassName}` : ""
      )}
    >
      <div className={styles.inner}>
        {/* Toggle Filter (KA/BH) */}
        {options && (
          <div className={styles.toggleGroup}>
            {options.map((option) => (
              <Button
                key={option}
                onClick={() => setSelected(option)}
                className={`${styles.toggleButton} ${
                  selected === option ? styles.active : ""
                }`}
              >
                {option}
              </Button>
            ))}
          </div>
        )}

        {/* Other Filters */}
        {/* Dynamic Filter Tags */}
        {filterConfigs?.map((filter) => (
          <FilterTag
            key={filter.key}
            label={filter.label}
            prefix={filter.prefix}
            onClick={() => setActiveFilterKey(filter.key)}
          />
        ))}
        {(Object.keys(selectedMap).length > 0 || selected !== options?.[0]) && (
          <Button
            onClick={handleClear}
            className={styles.toggleButton}
            variant="secondary"
            type="button"
          >
            Clear
          </Button>
        )}
      </div>
      {activeFilter && (
        <ModalManager
          isOpen={!!activeFilter}
          onClose={() => setActiveFilterKey(null)}
          title={activeFilter.modalTitle}
          customClass={styles.modalContent}
          modalHeaderClass={styles.modalHeader}
        >
          <SortModal
            options={activeFilter.options}
            multiSelect={activeFilter.multiSelect}
            withPriceRange={activeFilter.withPriceRange}
            maxPrice={
              activeFilter.withPriceRange ? activeFilter.maxPrice : undefined
            }
            defaultSelected={selectedMap[activeFilter.key] || []}
            onApply={(selectedValues) =>
              handleApply(activeFilter.key, selectedValues)
            }
            withClear={activeFilter.withClear}
          />
        </ModalManager>
      )}
    </div>
  );
}
